import { createIcons, UserPlus, KeyRound, PenLine, ShieldCheck, Search, X, Building2, AlertTriangle } from "lucide";
import { contextStore, getRoleDisplayName } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError } from "../../shared/errors/app-error.js";
import {
  PEOPLE_ACCOUNT_STATUS_LABELS,
  PEOPLE_FILTERS,
  filterPeopleDirectory,
  searchPeopleDirectory,
  type PeopleFilter,
  type PeopleMigrationPendency,
  type ProfessionalDirectoryView,
  type RelinkResolution
} from "../../domain/identity/people-directory.js";
import { ProfessionalStatus, ProfessionalType } from "../../domain/identity/professional.js";
import { RoleCode, OFFICIAL_ROLES } from "../../domain/identity/access-types.js";
import {
  INDIVIDUAL_REVOCATION_UNSUPPORTED,
  PERMISSION_GROUP_LABELS,
  permissionsByGroup,
  searchPermissions,
  summarizeEffectivePermissions,
  type PermissionGroupKey
} from "../../domain/identity/permission-catalog.js";
import { resolveEffectivePermissions } from "../../permissions/effective-permissions.js";

/**
 * PESSOAS E ACESSOS — a tela única.
 *
 * Substitui, na prática, três listagens que mostravam a mesma pessoa três
 * vezes: Profissionais, Usuários e Signatários. Aqui cada linha é UMA pessoa.
 *
 *   PROFESSIONAL  → quem ela é profissionalmente (fonte mestre)
 *   USER          → a conta de acesso (0 ou 1, opcional)
 *   SIGNATÁRIO    → capacidade de assinar, derivada da assinatura cadastrada
 *                   e do papel no documento. NÃO é um cadastro de pessoa.
 *
 * Desempenho: a listagem consome a projeção leve
 * (`AdminService.listPeopleDirectory`) — sem imagem de assinatura, sem foto,
 * sem histórico. O cadastro completo só é lido ao abrir a pessoa.
 *
 * SUB-OS NÃO ENTRA AQUI. Não é exibida, consultada, filtrada nem editada
 * nesta tela — a responsabilidade por Sub-OS vive em Administração → OS.
 */
class PeopleAccessPage extends HTMLElement {
  private rows: ProfessionalDirectoryView[] = [];
  private pendencies: PeopleMigrationPendency[] = [];
  private clientNames = new Map<string, string>();
  private filter: PeopleFilter = "TODOS";
  private search = "";
  private isLoading = true;
  private loadError = "";
  private notice = "";

  /** Pessoa aberta (detalhe sob demanda) e o que já foi carregado dela. */
  private openPersonId: string | null = null;
  private detail: { professional: any; user: any; signature: string | null } | null = null;
  private detailTab: "DADOS" | "ACESSO" | "PERMISSOES" | "ASSINATURA" | "PLH" | "HISTORICO" = "DADOS";
  private detailLoading = false;

  /** Busca e filtro da aba Permissões — 208 códigos não se administram rolando. */
  private permissionQuery = "";
  private permissionGroup: PermissionGroupKey | "ALL" = "ALL";

  /**
   * Formulários de EDIÇÃO do cadastro e do acesso já existentes.
   *
   * `null` = a aba está em leitura. O estado vive aqui, e não no DOM, porque
   * a aba re-renderiza a cada tecla da busca de permissões e a cada aviso —
   * ler os valores do DOM na hora de salvar perderia o que foi digitado.
   */
  private editingPerson: { name: string; email: string; functionTitle: string; professionalType: string; crea: string; cft: string; snqc: string; status: string } | null = null;
  private editingAccess: { login: string; email: string; profileType: string; status: string } | null = null;

  /** RESOLVER PENDÊNCIA: conta órfã em análise e candidatos propostos. */
  private resolvingPendency: PeopleMigrationPendency | null = null;
  private relink: RelinkResolution | null = null;
  private relinkChoice = "";

  /**
   * Formulário único de NOVA PESSOA. O estado vive aqui porque o formulário
   * re-renderiza a cada mudança do bloco de acesso — sem isto, o que já foi
   * digitado nos outros blocos se perderia ao marcar "possui acesso".
   */
  private newPerson = {
    name: "", email: "", status: "ACTIVE",
    functionTitle: "", professionalType: String(ProfessionalType.Inspector), crea: "", cft: "", snqc: "",
    hasAccess: false, login: "", accessEmail: "", password: "", profileType: String(RoleCode.Viewer)
  };

  /**
   * Anomalias do `Professional.plhClientId` legado. Aparece só quando existe
   * alguma — uma base limpa não mostra painel de migração.
   */
  private legacyPlh: {
    diverge: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string; clientResponsiblePlhProfessionalId: string }>;
    orphans: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string }>;
  } | null = null;
  private legacyPlhBusy = false;

  private showNewPerson = false;
  private showNewAccessFor: string | null = null;
  /** Redefinição de senha pelo administrador. */
  private resetPasswordFor: { userId: string; personName: string; login: string } | null = null;
  private busy = false;

  private get platform(): any { return (window as any).app?.platform; }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private can(permission: string): boolean { return contextStore.hasPermission(permission); }
  /** Quem pode cadastrar/remover a assinatura de uma pessoa. */
  private canManage(): boolean { return this.can(PermissionCode.IdentityManage); }
  /** Recusa da última tentativa de anexar assinatura, mostrada na aba. */
  private signatureError = "";

  connectedCallback() {
    this.render();
    void this.load();
  }

  private async load() {
    this.isLoading = true;
    this.loadError = "";
    this.render();
    try {
      const [rows, pendencies, clients, legacy] = await Promise.all([
        this.platform.adminService.listPeopleDirectory(this.ctx()),
        this.platform.adminService.listPeopleMigrationPendencies(this.ctx()).catch(() => []),
        this.platform.adminService.listClients(this.ctx()).catch(() => []),
        // Só relata na carga: nada é alterado sem o usuário mandar.
        this.platform.adminService.cleanupLegacyPlhAnomalies(this.ctx(), { apply: false }).catch(() => null)
      ]);
      this.rows = rows;
      this.pendencies = pendencies;
      this.legacyPlh = legacy && (legacy.diverge.length > 0 || legacy.orphans.length > 0)
        ? { diverge: legacy.diverge, orphans: legacy.orphans }
        : null;
      this.clientNames = new Map((clients || []).map((c: any) => [String(c.id), c.name || c.code || String(c.id)]));
    } catch (e) {
      this.loadError = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível carregar Pessoas e Acessos.";
    }
    this.isLoading = false;
    this.render();
  }

  /** Detalhe sob demanda (item 31): só aqui se lê o cadastro completo e a assinatura. */
  private async openPerson(professionalId: string) {
    this.openPersonId = professionalId;
    this.detail = null;
    this.detailTab = "DADOS";
    this.detailLoading = true;
    this.render();
    try {
      const row = this.rows.find(r => r.professionalId === professionalId);
      const professional = await this.platform.adminService.getProfessional?.(this.ctx(), professionalId)
        ?? (await this.platform.adminService.listProfessionals(this.ctx())).find((p: any) => String(p.id) === professionalId);
      const user = row?.userId
        ? (await this.platform.adminService.listUsers(this.ctx())).find((u: any) => String(u.id) === row.userId) ?? null
        : null;
      const signature = professional?.signatureImageId
        ? await this.platform.adminService.getProfessionalSignature?.(this.ctx(), professionalId).catch(() => null) ?? null
        : null;
      this.detail = { professional, user, signature };
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível abrir o cadastro.";
    }
    this.detailLoading = false;
    this.render();
  }

  private visibleRows(): ProfessionalDirectoryView[] {
    return searchPeopleDirectory(filterPeopleDirectory(this.rows, this.filter), this.search);
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
  }

  private clientLabel(id: string): string { return this.clientNames.get(id) || id; }

  // ------------------------------------------------------------- RENDER

  render() {
    const canManage = this.can(PermissionCode.IdentityManage);
    const canManageUsers = this.can(PermissionCode.UserManage);
    const rows = this.visibleRows();

    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="pa-head">
        <div>
          <h1>Pessoas e Acessos</h1>
          <p>Cadastro único de pessoas: dados profissionais, conta de acesso, assinatura e responsabilidade PLH. Uma pessoa, um registro.</p>
        </div>
        <div class="pa-head-actions">
          ${canManage ? `<button class="pa-btn primary" id="btn-new-person"><i data-lucide="user-plus"></i> NOVA PESSOA</button>` : ""}
        </div>
      </div>

      ${this.pendencies.length > 0 ? `
        <div class="pa-pendency">
          <i data-lucide="alert-triangle"></i>
          <div>
            <strong>${this.pendencies.length} pendência(s) de migração</strong>
            <p>Registros que parecem ser da mesma pessoa, mas sem vínculo formal. O sistema não une fichas por nome igual — a conferência é manual.</p>
            <ul>${this.pendencies.slice(0, 5).map((p, i) => `
              <li>
                <b>${this.esc(p.name)}</b> — ${this.esc(p.detail)}
                ${p.userId && this.can(PermissionCode.IdentityManage) ? `<button class="pa-btn sm" data-resolve="${i}">RESOLVER PENDÊNCIA</button>` : ""}
              </li>`).join("")}</ul>
          </div>
        </div>` : ""}

      ${this.renderLegacyPlhPanel()}

      <div class="pa-toolbar">
        <div class="pa-search">
          <i data-lucide="search"></i>
          <input id="pa-search" type="search" placeholder="Buscar por nome, login, e-mail ou registro" value="${this.esc(this.search)}">
        </div>
        <div class="pa-filters">
          ${PEOPLE_FILTERS.map(f => `<button class="pa-chip ${this.filter === f.value ? "active" : ""}" data-filter="${f.value}">${this.esc(f.label)}</button>`).join("")}
        </div>
      </div>

      ${this.notice ? `<div class="pa-notice">${this.esc(this.notice)}</div>` : ""}
      ${this.loadError ? `<div class="pa-error">${this.esc(this.loadError)}</div>` : ""}

      ${this.isLoading ? `<div class="pa-empty">Carregando pessoas…</div>` : rows.length === 0 ? `
        <div class="pa-empty">Nenhuma pessoa encontrada para este filtro.</div>
      ` : `
        <table class="pa-table">
          <thead>
            <tr>
              <th>Nome</th><th>Função</th><th>Profissão</th><th>Conselho</th><th>Registro</th>
              <th>Usuário</th><th>Acesso</th><th>Perfil</th><th>PLH</th><th>Assinatura</th><th>Situação</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(r => this.renderRow(r, canManageUsers)).join("")}
          </tbody>
        </table>
        <div class="pa-count">${rows.length} de ${this.rows.length} pessoa(s)</div>
      `}

      ${this.openPersonId ? this.renderDetail() : ""}
      ${this.showNewPerson ? this.renderNewPersonModal() : ""}
      ${this.showNewAccessFor ? this.renderNewAccessModal() : ""}
      ${this.resolvingPendency ? this.renderResolveModal() : ""}
      ${this.resetPasswordFor ? this.renderResetPasswordModal() : ""}
    `;

    createIcons({ icons: { UserPlus, KeyRound, PenLine, ShieldCheck, Search, X, Building2, AlertTriangle }, nameAttr: "data-lucide", root: this });
    this.attachEvents();
  }

  private renderRow(r: ProfessionalDirectoryView, canManageUsers: boolean): string {
    const accountClass = r.accountStatus === "ATIVO" ? "ok" : r.accountStatus === "SEM_ACESSO" ? "muted" : "warn";
    return `
      <tr data-person="${this.esc(r.professionalId)}">
        <td class="pa-name">${this.esc(r.name)}</td>
        <td>${this.esc(r.jobTitle) || "—"}</td>
        <td>${this.esc(r.profession) || "—"}</td>
        <td>${this.esc(r.council) || "—"}</td>
        <td>${this.esc(r.registration) || "—"}</td>
        <td>${r.username ? this.esc(r.username) : "—"}</td>
        <td><span class="pa-tag ${accountClass}">${this.esc(PEOPLE_ACCOUNT_STATUS_LABELS[r.accountStatus])}</span></td>
        <td>${r.roleSummary ? this.esc(getRoleDisplayName(r.roleSummary)) : "—"}</td>
        <td>${r.isPlh ? `<span class="pa-tag ok">SIM${r.plhClientIds.length > 0 ? ` · ${r.plhClientIds.length}` : ""}</span>` : "—"}</td>
        <td>${r.hasSignature ? `<span class="pa-tag ok">Cadastrada</span>` : `<span class="pa-tag muted">Não</span>`}</td>
        <td><span class="pa-tag ${r.professionalStatus === ProfessionalStatus.Active ? "ok" : "warn"}">${r.professionalStatus === ProfessionalStatus.Active ? "Ativo" : "Inativo"}</span></td>
        <td class="pa-row-actions">
          ${!r.userId && canManageUsers ? `<button class="pa-btn sm" data-create-access="${this.esc(r.professionalId)}"><i data-lucide="key-round"></i> Criar acesso</button>` : ""}
          <button class="pa-btn sm ghost" data-open="${this.esc(r.professionalId)}">Abrir</button>
          ${this.can(PermissionCode.ProfessionalUpdate) || this.can(PermissionCode.IdentityManage)
            ? `<button class="pa-btn sm ghost" data-open-edit="${this.esc(r.professionalId)}" title="Abrir o cadastro em edição"><i data-lucide="pen-line"></i> Editar</button>`
            : ""}
        </td>
      </tr>`;
  }

  /**
   * ABA PERMISSÕES — o que a pessoa tem, de onde vem, e o que dá para mudar.
   *
   * Antes era a lista crua de `customPermissions` separada por "•". Com 208
   * códigos, escolher entre `me_report.approve` e `technical_review.approve`
   * era tentativa e erro, e errar aqui concede ou nega acesso de verdade.
   *
   * Agora: contagem de efetivas, agrupamento por domínio, busca, e cada linha
   * com o nome em português E o código ao lado. As herdadas do perfil ficam
   * travadas — desmarcá-las não teria efeito (ver
   * INDIVIDUAL_REVOCATION_UNSUPPORTED), e um controle que mente é pior que um
   * controle ausente.
   */
  /**
   * ABA DADOS — leitura ou EDIÇÃO do Professional existente.
   *
   * A edição é `UPDATE` do registro: `professionalId` não muda, e nenhum
   * Professional novo é criado. Excluir e recriar pareceria equivalente e
   * quebraria tudo o que aponta para o id — inspeções, relatórios,
   * assinaturas, aprovações, e a responsabilidade PLH do Cliente.
   */
  private renderPersonDataTab(row: ProfessionalDirectoryView, p: any): string {
    const canEdit = this.can(PermissionCode.ProfessionalUpdate) || this.can(PermissionCode.IdentityManage);

    if (!this.editingPerson) {
      return `<div class="pa-grid">
          ${this.pField("Nome", p?.name ?? row.name)}${this.pField("E-mail profissional", p?.email ?? row.email)}
          ${this.pField("Função", p?.functionTitle ?? row.jobTitle)}${this.pField("Profissão", p?.professionalType ?? row.profession)}
          ${this.pField("Conselho", row.council)}${this.pField("Registro", row.registration)}
          ${this.pField("Situação", row.professionalStatus === ProfessionalStatus.Active ? "Ativo" : "Inativo")}
        </div>
        ${canEdit ? `<div class="pa-actions"><button class="pa-btn" data-edit-person="${this.esc(row.professionalId)}"><i data-lucide="pen-line"></i> EDITAR CADASTRO</button></div>` : ""}`;
    }

    const f = this.editingPerson;
    const input = (name: string, label: string, value: string) =>
      `<label class="pa-field"><span>${this.esc(label)}</span><input class="pa-input" data-person-field="${name}" value="${this.esc(value)}"></label>`;

    return `
      <div class="pa-grid">
        ${input("name", "Nome", f.name)}
        ${input("email", "E-mail profissional", f.email)}
        ${input("functionTitle", "Função", f.functionTitle)}
        <label class="pa-field"><span>Profissão</span>
          <select class="pa-input" data-person-field="professionalType">
            ${Object.values(ProfessionalType).map(t => `<option value="${t}" ${f.professionalType === t ? "selected" : ""}>${t}</option>`).join("")}
          </select>
        </label>
        ${input("crea", "CREA", f.crea)}
        ${input("cft", "CFT", f.cft)}
        ${input("snqc", "SNQC", f.snqc)}
        <label class="pa-field"><span>Situação</span>
          <select class="pa-input" data-person-field="status">
            <option value="${ProfessionalStatus.Active}" ${f.status === ProfessionalStatus.Active ? "selected" : ""}>Ativo</option>
            <option value="${ProfessionalStatus.Inactive}" ${f.status === ProfessionalStatus.Inactive ? "selected" : ""}>Inativo</option>
          </select>
        </label>
      </div>
      <p class="pa-hint">O cadastro é ATUALIZADO: o identificador do Profissional não muda, e nada que aponta para ele — inspeções, relatórios, assinaturas, responsabilidade PLH do Cliente — é afetado.</p>
      <div class="pa-actions">
        <button class="pa-btn" data-cancel-edit-person="1">CANCELAR</button>
        <button class="pa-btn primary" data-save-person="${this.esc(row.professionalId)}" ${this.busy ? "disabled" : ""}><i data-lucide="save"></i> ${this.busy ? "SALVANDO..." : "SALVAR CADASTRO"}</button>
      </div>`;
  }

  /**
   * ABA ACESSO — leitura ou EDIÇÃO da conta existente.
   *
   * `userId` é preservado, e o vínculo com o Professional também: trocar o
   * Professional de uma conta existente mudaria a AUTORIA de documentos já
   * emitidos, então não é oferecido aqui.
   */
  private renderAccessTab(row: ProfessionalDirectoryView, u: any): string {
    const canManage = this.can(PermissionCode.UserManage) || this.can(PermissionCode.IdentityManage);
    const active = String(u.status ?? "ACTIVE").toUpperCase() === "ACTIVE";

    if (!this.editingAccess) {
      return `<div class="pa-grid">
          ${this.pField("Login", u.login || u.username)}${this.pField("E-mail de acesso", u.email)}
          ${this.pField("Perfil", getRoleDisplayName(u.profileType))}${this.pField("Situação da conta", PEOPLE_ACCOUNT_STATUS_LABELS[row.accountStatus])}
        </div>
        ${canManage ? `<div class="pa-actions">
          <button class="pa-btn" data-edit-access="${this.esc(u.id)}"><i data-lucide="pen-line"></i> EDITAR ACESSO</button>
          <button class="pa-btn" data-reset-password="${this.esc(u.id)}"><i data-lucide="key-round"></i> REDEFINIR SENHA</button>
          <button class="pa-btn ${active ? "" : "primary"}" data-toggle-account="${this.esc(u.id)}">${active ? "INATIVAR CONTA" : "REATIVAR CONTA"}</button>
        </div>` : ""}
        <p class="pa-hint">Desativar a conta não apaga nem inativa o Profissional: a pessoa continua no histórico documental, com o mesmo identificador.</p>`;
    }

    const f = this.editingAccess;
    return `
      <div class="pa-grid">
        <label class="pa-field"><span>Login</span><input class="pa-input" data-access-field="login" value="${this.esc(f.login)}"></label>
        <label class="pa-field"><span>E-mail de acesso</span><input class="pa-input" data-access-field="email" value="${this.esc(f.email)}"></label>
        <label class="pa-field"><span>Perfil</span>
          <select class="pa-input" data-access-field="profileType">
            ${OFFICIAL_ROLES.map(r => `<option value="${r.code}" ${f.profileType === r.code ? "selected" : ""}>${r.label.toUpperCase()}</option>`).join("")}
          </select>
        </label>
        <label class="pa-field"><span>Situação da conta</span>
          <select class="pa-input" data-access-field="status">
            <option value="ACTIVE" ${f.status === "ACTIVE" ? "selected" : ""}>Ativa</option>
            <option value="INACTIVE" ${f.status === "INACTIVE" ? "selected" : ""}>Inativa</option>
          </select>
        </label>
      </div>
      ${f.profileType !== String(u.profileType) ? `
        <div class="pa-warn-box">
          <strong>O PERFIL VAI MUDAR: ${this.esc(getRoleDisplayName(u.profileType))} → ${this.esc(getRoleDisplayName(f.profileType))}</strong>
          <p>As permissões herdadas serão recalculadas no próximo carregamento da sessão da pessoa.
          ${(u.customPermissions ?? []).length > 0
            ? `As <strong>${(u.customPermissions as string[]).length} permissão(ões) individual(is)</strong> dela são PRESERVADAS — nenhuma é apagada por esta troca.`
            : "Esta pessoa não possui permissões individuais."}</p>
        </div>` : ""}
      <p class="pa-hint">A conta é ATUALIZADA: o identificador do usuário e o vínculo com o Profissional não mudam. Trocar o Profissional de uma conta existente alteraria a autoria de documentos já emitidos e não é feito por aqui.</p>
      <div class="pa-actions">
        <button class="pa-btn" data-cancel-edit-access="1">CANCELAR</button>
        <button class="pa-btn primary" data-save-access="${this.esc(u.id)}" ${this.busy ? "disabled" : ""}><i data-lucide="save"></i> ${this.busy ? "SALVANDO..." : "SALVAR ACESSO"}</button>
      </div>`;
  }

  private pField(label: string, value: unknown): string {
    return `<div class="pa-field"><span>${this.esc(label)}</span><strong>${this.esc(value) || "—"}</strong></div>`;
  }

  private renderPermissionsTab(u: { id: string; profileType: string; customPermissions?: readonly string[] }): string {
    const canManage = this.can(PermissionCode.RbacManage) || this.can(PermissionCode.IdentityManage);
    const individual = [...(u.customPermissions ?? [])].map(String);
    const summary = summarizeEffectivePermissions(resolveEffectivePermissions(u.profileType), individual);

    const visible = searchPermissions(this.permissionQuery, this.permissionGroup);
    const visibleCodes = new Set(visible.map(p => p.code));
    const groups = permissionsByGroup(summary.rows.map(r => r.descriptor).filter(d => visibleCodes.has(d.code)));
    const rowOf = new Map(summary.rows.map(r => [r.descriptor.code, r]));

    const filters = (["ALL", ...Object.keys(PERMISSION_GROUP_LABELS)] as Array<"ALL" | PermissionGroupKey>)
      .map(key => `<button class="pa-chip ${this.permissionGroup === key ? "active" : ""}" data-perm-group="${key}">${key === "ALL" ? "Todas" : this.esc(PERMISSION_GROUP_LABELS[key as PermissionGroupKey])}</button>`)
      .join("");

    const line = (code: string) => {
      const row = rowOf.get(code);
      if (!row) return "";
      const fromRole = row.origin === "ROLE";
      const mark = fromRole ? "✓" : row.origin === "INDIVIDUAL" ? "+" : "";
      return `
        <label class="pa-perm-line ${row.granted ? "on" : ""}" title="${fromRole ? "Herdada do perfil — altere o perfil para retirar." : "Permissão individual"}">
          <input type="checkbox" data-perm-code="${this.esc(code)}"
                 ${row.granted ? "checked" : ""} ${fromRole || !canManage ? "disabled" : ""}>
          <span class="pa-perm-mark">${mark}</span>
          <span class="pa-perm-name">${this.esc(row.descriptor.label)}</span>
          <code>${this.esc(code)}</code>
        </label>`;
    };

    return `
      <div class="pa-grid">
        ${this.pField("Perfil", getRoleDisplayName(u.profileType))}
        ${this.pField("Herdadas do perfil", String(summary.fromRole))}
        ${this.pField("Individuais", String(summary.individual))}
        ${this.pField("Efetivas", String(summary.effective))}
      </div>
      <p class="pa-hint">✓ herdada do perfil · + concedida individualmente. ${this.esc(INDIVIDUAL_REVOCATION_UNSUPPORTED)}</p>
      <div class="pa-perm-toolbar">
        <input class="pa-input" id="perm-search" placeholder="Buscar permissão…" value="${this.esc(this.permissionQuery)}">
        <div class="pa-chips">${filters}</div>
      </div>
      ${groups.length === 0 ? `<div class="pa-empty-inline"><p>Nenhuma permissão corresponde à pesquisa.</p></div>` : ""}
      ${groups.map(g => `
        <div class="pa-perm-group">
          <div class="pa-perm-group-head">${this.esc(g.label)} <small>${g.permissions.length}</small></div>
          ${g.permissions.map(p => line(p.code)).join("")}
        </div>`).join("")}
      ${canManage ? `
        <div class="pa-actions">
          <button class="pa-btn" data-perm-restore="${this.esc(u.id)}"><i data-lucide="rotate-ccw"></i> RESTAURAR PERMISSÕES DO PERFIL</button>
          <button class="pa-btn primary" data-perm-save="${this.esc(u.id)}"><i data-lucide="save"></i> SALVAR PERMISSÕES</button>
        </div>`
        : `<p class="pa-hint">Somente quem administra perfis e permissões pode alterar esta aba.</p>`}`;
  }

  /** Detalhe: as seis seções do cadastro. Nenhuma delas é de Sub-OS. */
  private renderDetail(): string {
    const row = this.rows.find(r => r.professionalId === this.openPersonId);
    if (!row) return "";
    const tabs: Array<[typeof this.detailTab, string]> = [
      ["DADOS", "Dados profissionais"], ["ACESSO", "Acesso"], ["PERMISSOES", "Permissões"],
      ["ASSINATURA", "Assinatura"], ["PLH", "Responsabilidade PLH"], ["HISTORICO", "Histórico"]
    ];
    return `
      <div class="pa-modal-backdrop">
        <div class="pa-modal wide">
          <div class="pa-modal-head">
            <span>${this.esc(row.name)}</span>
            <button class="pa-icon-btn" id="pa-close-detail"><i data-lucide="x"></i></button>
          </div>
          <div class="pa-tabs">
            ${tabs.map(([id, label]) => `<button class="pa-tab ${this.detailTab === id ? "active" : ""}" data-tab="${id}">${label}</button>`).join("")}
          </div>
          <div class="pa-modal-body">
            ${this.detailLoading ? `<div class="pa-empty">Carregando cadastro…</div>` : this.renderDetailBody(row)}
          </div>
        </div>
      </div>`;
  }

  private renderDetailBody(row: ProfessionalDirectoryView): string {
    const p = this.detail?.professional;
    const u = this.detail?.user;
    const field = (label: string, value: unknown) => `<div class="pa-field"><span>${this.esc(label)}</span><strong>${this.esc(value) || "—"}</strong></div>`;

    switch (this.detailTab) {
      case "DADOS":
        return this.renderPersonDataTab(row, p);

      case "ACESSO":
        return u
          ? this.renderAccessTab(row, u)
          : `<div class="pa-empty-inline">
              <p><strong>Esta pessoa não possui conta de acesso.</strong></p>
              <p>Um Profissional pode existir apenas para fins documentais — assinar relatórios, constar como Inspetor responsável — sem nunca entrar no sistema.</p>
              ${this.can(PermissionCode.UserManage) ? `<button class="pa-btn primary" data-create-access="${this.esc(row.professionalId)}"><i data-lucide="key-round"></i> CRIAR ACESSO</button>` : ""}
            </div>`;

      case "PERMISSOES":
        return u
          ? this.renderPermissionsTab(u)
          : `<div class="pa-empty-inline"><p>Sem conta de acesso, não há permissões a exibir.</p></div>`;

      /**
       * A aba apenas EXIBIA a assinatura. Não havia controle para anexar,
       * embora o gate de PLH exija `signatureImageId` e a própria tela
       * dissesse que a assinatura "é cadastrada ao abrir a pessoa" — com
       * isso nenhum Engenheiro podia ser nomeado PLH de Cliente nenhum.
       */
      case "ASSINATURA":
        return `<div class="pa-signature">
            ${this.detail?.signature
              ? `<img src="${this.detail.signature}" alt="Assinatura cadastrada">`
              : `<div class="pa-empty-inline"><p>Nenhuma assinatura cadastrada para esta pessoa.</p></div>`}
          </div>
          ${this.canManage()
            ? `<div class="pa-signature-actions">
                 <label class="pa-btn" for="pa-signature-file">${this.detail?.signature ? "Substituir assinatura" : "Anexar assinatura"}</label>
                 <input id="pa-signature-file" type="file" accept="image/png,image/jpeg" hidden>
                 ${this.detail?.signature ? `<button type="button" class="pa-btn pa-btn-ghost" id="pa-signature-remove">Remover</button>` : ""}
                 <span class="pa-hint">PNG ou JPEG, até 2 MB.</span>
               </div>
               ${this.signatureError ? `<p class="pa-error">${this.esc(this.signatureError)}</p>` : ""}`
            : `<p class="pa-hint">Você não possui permissão para alterar a assinatura desta pessoa.</p>`}
          <p class="pa-hint">A assinatura pertence ao Profissional — não à conta de acesso nem a um cadastro separado de signatário. Ela é exigida para aprovar e assinar documentos, e para ser nomeado PLH responsável de um Cliente. Documentos já finalizados preservam a assinatura vigente no momento da aprovação.</p>`;

      case "PLH":
        return `<div class="pa-grid">
            ${field("Elegível como PLH", row.isPlh ? "Sim" : "Não")}
            ${field("Cliente de habilitação", p?.plhClientId ? this.clientLabel(String(p.plhClientId)) : "—")}
          </div>
          <div class="pa-clients">
            <strong>Clientes sob responsabilidade</strong>
            ${row.plhClientIds.length === 0
              ? `<p class="pa-hint">Nenhum Cliente nomeou esta pessoa como PLH responsável.</p>`
              : `<ul>${row.plhClientIds.map(id => `<li><i data-lucide="building-2"></i> ${this.esc(this.clientLabel(id))}</li>`).join("")}</ul>`}
          </div>
          <p class="pa-hint">A responsabilidade é definida no cadastro do <strong>Cliente</strong> (Administração → Clientes). Esta tela apenas mostra o resultado — não cria um segundo vínculo.</p>
          ${row.isPlh && !row.userId ? `<div class="pa-warn"><strong>PLH SEM ACESSO AO SISTEMA</strong><p>O PLH responsável está cadastrado, mas não possui uma conta ativa para realizar a aprovação.</p></div>` : ""}`;

      case "HISTORICO":
      default:
        return `<div class="pa-empty-inline">
            <p>O histórico desta pessoa (criação, alterações, ativação, acesso, perfil, permissões, assinatura e vínculos como PLH) é lido da trilha de auditoria.</p>
            <p class="pa-hint">No ambiente de teste local a trilha não é persistida — ver o aviso sobre auditoria no manual do projeto.</p>
          </div>`;
    }
  }

  /**
   * RESOLVER PENDÊNCIA — o vínculo certo de uma conta órfã.
   *
   * Mostra a conta, o `professionalId` inválido e os candidatos reais, com o
   * critério que os trouxe (registro profissional ou e-mail). Nunca propõe
   * candidato por nome. Havendo mais de um, nada é pré-selecionado: quem
   * decide é a pessoa, não o sistema.
   */
  private renderResolveModal(): string {
    const pendency = this.resolvingPendency;
    if (!pendency) return "";
    const resolution = this.relink;
    const criterion = (c: string) => c === "REGISTRO_PROFISSIONAL" ? "registro profissional"
      : c === "EMAIL_PROFISSIONAL" ? "e-mail profissional" : "e-mail + registro";
    return `
      <div class="pa-modal-backdrop">
        <div class="pa-modal">
          <div class="pa-modal-head"><span>RESOLVER PENDÊNCIA — ${this.esc(pendency.name)}</span><button class="pa-icon-btn" id="pa-close-resolve"><i data-lucide="x"></i></button></div>
          <div class="pa-modal-body">
            <div class="pa-grid">
              <div class="pa-field"><span>Conta de acesso</span><strong>${this.esc(pendency.userId)}</strong></div>
              <div class="pa-field"><span>Professional inválido atual</span><strong>${this.esc(pendency.professionalId) || "—"}</strong></div>
            </div>
            ${!resolution ? `<div class="pa-empty">Procurando candidatos…</div>` : `
              <p class="pa-hint">${this.esc(resolution.reason)}</p>
              ${resolution.candidates.length === 0 ? `
                <div class="pa-warn"><strong>NENHUM CADASTRO CORRESPONDENTE</strong>
                  <p>Nenhum Profissional bate por registro profissional ou e-mail. Cadastre a pessoa em NOVA PESSOA e volte aqui, ou confira os dados da conta.</p>
                </div>` : `
                <div class="pa-form" style="grid-template-columns:1fr;">
                  <label><span>Vincular a qual Profissional?</span>
                    <select id="pa-relink-choice">
                      <option value="">Selecione o cadastro correto</option>
                      ${resolution.candidates.map(c => `<option value="${this.esc(c.professionalId)}" ${this.relinkChoice === c.professionalId ? "selected" : ""}>${this.esc(c.name)}${c.registration ? ` — ${this.esc(c.council)} ${this.esc(c.registration)}` : ""}${c.status !== ProfessionalStatus.Active ? " — INATIVO" : ""} · casou por ${this.esc(c.matchedBy.map(criterion).join(" e "))}</option>`).join("")}
                    </select>
                  </label>
                </div>
                ${resolution.requiresManualReview ? `<p class="pa-hint">Mais de um candidato: a escolha não é automática. Confira o registro profissional antes de confirmar.</p>` : ""}
              `}
            `}
            ${this.notice ? `<div class="pa-error">${this.esc(this.notice)}</div>` : ""}
            <p class="pa-hint">Esta ação corrige apenas a referência. Nenhuma conta é apagada, nenhum Profissional é criado e o login, o perfil e o histórico documental são preservados.</p>
            <div class="pa-actions">
              <button class="pa-btn" id="pa-cancel-resolve" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="pa-btn primary" id="pa-confirm-resolve" ${this.busy || !this.relinkChoice ? "disabled" : ""}>${this.busy ? "VINCULANDO…" : "VINCULAR AO PROFESSIONAL"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  /**
   * NOVA PESSOA — um formulário só, em blocos.
   *
   * Antes eram dois passos: cadastrar a pessoa e, depois, criar o acesso numa
   * segunda tela. Aqui a pessoa é cadastrada com tudo o que já se sabe dela —
   * inclusive a conta, quando houver — numa passagem só.
   *
   * O bloco da conta é OPCIONAL de propósito: um Profissional pode existir
   * apenas para fins documentais, assinando relatórios sem nunca entrar no
   * sistema.
   *
   * Não há campo de PLH aqui. Ser PLH é ter sido NOMEADO por um Cliente
   * (`Client.responsiblePlhProfessionalId`); oferecer o vínculo neste
   * formulário recriaria a segunda fonte que a arquitetura eliminou.
   */
  /**
   * MIGRAÇÃO DO PLH LEGADO.
   *
   * `Professional.plhClientId` deixou de ser lido em 2026-09-24 — a
   * habilitação vive em `Client.responsiblePlhProfessionalId`. Os valores
   * antigos ficaram gravados e podem discordar do Cliente. O painel separa os
   * dois casos porque eles não têm a mesma solução:
   *
   *   SUPERADOS  — o Cliente nomeou outra pessoa. Podem ir embora.
   *   SEM DONO   — o Cliente não nomeou ninguém, e o valor legado é a única
   *                pista de quem respondia. Não se apaga nem se promove: quem
   *                decide é uma pessoa, no cadastro do Cliente.
   */
  private renderLegacyPlhPanel(): string {
    const legacy = this.legacyPlh;
    if (!legacy) return "";
    const canManage = this.can(PermissionCode.IdentityManage);
    return `
      <div class="pa-legacy">
        <div class="pa-legacy-head">MIGRAÇÃO — HABILITAÇÃO DE PLH ANTIGA</div>
        <p class="pa-hint">O cadastro antigo guardava a habilitação de PLH no Profissional. Hoje ela é do Cliente. Estes registros ficaram para trás.</p>

        ${legacy.diverge.length > 0 ? `
          <div class="pa-legacy-group">
            <strong>${legacy.diverge.length} habilitação(ões) superada(s)</strong>
            <p class="pa-hint">O Cliente já nomeou outra pessoa. Nada depende mais destes valores.</p>
            <ul>${legacy.diverge.map(d => `<li>${this.esc(d.professionalName)} → ${this.esc(d.clientName)}, que nomeou outra pessoa</li>`).join("")}</ul>
            ${canManage ? `<button class="pa-btn primary" id="pa-legacy-apply" ${this.legacyPlhBusy ? "disabled" : ""}>${this.legacyPlhBusy ? "LIMPANDO…" : "LIMPAR HABILITAÇÕES SUPERADAS"}</button>` : ""}
          </div>` : ""}

        ${legacy.orphans.length > 0 ? `
          <div class="pa-legacy-group">
            <strong>${legacy.orphans.length} Cliente(s) sem PLH nomeado</strong>
            <p class="pa-hint">O valor antigo é a única pista de quem respondia. <strong>Não é apagado nem promovido</strong> — nomeie o responsável em Administração → Clientes.</p>
            <ul>${legacy.orphans.map(o => `<li>${this.esc(o.clientName)} — o antigo era ${this.esc(o.professionalName)}</li>`).join("")}</ul>
          </div>` : ""}
      </div>`;
  }

  /** Limpa só os superados. Os sem dono ficam para decisão humana. */
  private async applyLegacyPlhCleanup() {
    if (this.legacyPlhBusy) return;
    this.legacyPlhBusy = true;
    this.notice = "";
    this.render();
    try {
      const result = await this.platform.adminService.cleanupLegacyPlhAnomalies(this.ctx(), { apply: true });
      this.legacyPlhBusy = false;
      await this.load();
      this.notice = result.orphans.length > 0
        ? `${result.cleared} habilitação(ões) superada(s) removida(s). Restam ${result.orphans.length} Cliente(s) sem PLH nomeado, que dependem de decisão no cadastro do Cliente.`
        : `${result.cleared} habilitação(ões) superada(s) removida(s).`;
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível limpar as habilitações antigas.";
      this.legacyPlhBusy = false;
    }
    this.render();
  }

  /**
   * REDEFINIR SENHA. A senha nova é derivada pelo mecanismo oficial; o
   * administrador não vê nem escolhe formato. A confirmação existe para que
   * um erro de digitação não tranque a pessoa fora do sistema.
   */
  private renderResetPasswordModal(): string {
    const target = this.resetPasswordFor;
    if (!target) return "";
    return `
      <div class="pa-modal-backdrop">
        <div class="pa-modal">
          <div class="pa-modal-head"><span>REDEFINIR SENHA — ${this.esc(target.personName)}</span><button class="pa-icon-btn" id="pa-close-reset"><i data-lucide="x"></i></button></div>
          <div class="pa-modal-body">
            <div class="pa-grid">
              <div class="pa-field"><span>Conta</span><strong>${this.esc(target.login) || this.esc(target.userId)}</strong></div>
            </div>
            <div class="pa-form" style="grid-template-columns:1fr;">
              <label><span>Nova senha *</span><input id="rp-password" type="password" autocomplete="new-password"></label>
              <label><span>Repita a nova senha *</span><input id="rp-confirm" type="password" autocomplete="new-password"></label>
            </div>
            <p class="pa-hint">Mínimo de 8 caracteres. A senha é gravada derivada — ninguém, nem o administrador, consegue lê-la depois. A troca é registrada na auditoria (quem e quando, nunca a senha).</p>
            ${this.notice ? `<div class="pa-error">${this.esc(this.notice)}</div>` : ""}
            <div class="pa-actions">
              <button class="pa-btn" id="pa-cancel-reset" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="pa-btn primary" id="pa-confirm-reset" ${this.busy ? "disabled" : ""}>${this.busy ? "SALVANDO…" : "REDEFINIR SENHA"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private async confirmResetPassword() {
    const target = this.resetPasswordFor;
    if (!target || this.busy) return;
    const senha = this.value("#rp-password");
    const confirma = this.value("#rp-confirm");
    if (senha !== confirma) { this.notice = "As duas senhas não são iguais."; this.render(); return; }
    if (senha.length < 8) { this.notice = "A nova senha precisa ter pelo menos 8 caracteres."; this.render(); return; }

    this.busy = true; this.notice = ""; this.render();
    try {
      await this.platform.adminService.resetUserPassword(this.ctx(), target.userId, senha);
      this.resetPasswordFor = null;
      this.busy = false;
      this.notice = `Senha de ${target.personName} redefinida. Informe a nova senha à pessoa por um canal seguro.`;
      this.render();
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível redefinir a senha.";
      this.busy = false;
      this.render();
    }
  }

  private renderNewPersonModal(): string {
    const types = Object.values(ProfessionalType);
    const roles = Object.values(RoleCode);
    const f = this.newPerson;
    const block = (num: string, title: string, body: string, note = "") => `
      <section class="pa-block">
        <div class="pa-block-head"><span class="pa-block-num">${num}</span>${this.esc(title)}</div>
        ${note ? `<p class="pa-hint">${note}</p>` : ""}
        ${body}
      </section>`;

    return `
      <div class="pa-modal-backdrop">
        <div class="pa-modal wide">
          <div class="pa-modal-head"><span>NOVA PESSOA</span><button class="pa-icon-btn" id="pa-close-new"><i data-lucide="x"></i></button></div>
          <div class="pa-modal-body">

            ${block("1", "Identificação da pessoa", `
              <div class="pa-form">
                <label><span>Nome completo *</span><input id="np-name" type="text" value="${this.esc(f.name)}"></label>
                <label><span>E-mail principal</span><input id="np-email" type="email" value="${this.esc(f.email)}"></label>
                <label><span>Situação</span>
                  <select id="np-status">
                    <option value="ACTIVE" ${f.status === "ACTIVE" ? "selected" : ""}>Ativo</option>
                    <option value="INACTIVE" ${f.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
                  </select>
                </label>
              </div>`)}

            ${block("2", "Dados profissionais", `
              <div class="pa-form">
                <label><span>Função *</span><input id="np-function" type="text" placeholder="Ex.: Inspetor de Equipamentos" value="${this.esc(f.functionTitle)}"></label>
                <label><span>Profissão</span><select id="np-type">${types.map(t => `<option value="${t}" ${f.professionalType === t ? "selected" : ""}>${t}</option>`).join("")}</select></label>
                <label><span>CREA</span><input id="np-crea" type="text" value="${this.esc(f.crea)}"></label>
                <label><span>CFT</span><input id="np-cft" type="text" value="${this.esc(f.cft)}"></label>
                <label><span>SNQC</span><input id="np-snqc" type="text" value="${this.esc(f.snqc)}"></label>
              </div>`)}

            ${block("3", "Acesso ao sistema", `
              <label class="pa-toggle"><input type="checkbox" id="np-has-access" ${f.hasAccess ? "checked" : ""}> POSSUI ACESSO AO SISTEMA</label>
              ${f.hasAccess ? `
                <div class="pa-form">
                  <label><span>Login *</span><input id="np-login" type="text" value="${this.esc(f.login)}"></label>
                  <label><span>E-mail de acesso</span><input id="np-access-email" type="email" value="${this.esc(f.accessEmail)}" placeholder="Usa o e-mail principal quando vazio"></label>
                  <label><span>Senha inicial *</span><input id="np-password" type="password" autocomplete="new-password" value="${this.esc(f.password)}"></label>
                </div>` : ""}`,
              f.hasAccess ? "" : "Sem marcar, nenhuma conta é criada — a pessoa existe só no cadastro profissional.")}

            ${f.hasAccess ? block("4", "Perfil e permissões", `
              <div class="pa-form">
                <label><span>Perfil principal *</span><select id="np-role">${OFFICIAL_ROLES.map(r => `<option value="${r.code}" ${f.profileType === r.code ? "selected" : ""}>${r.label.toUpperCase()}</option>`).join("")}</select></label>
              </div>`,
              "As permissões vêm do perfil, pelo Permission Service oficial. Permissões individuais são concedidas depois, no cadastro da pessoa — não há matriz paralela.") : ""}

            ${block(f.hasAccess ? "5" : "4", "Responsabilidade PLH", `
              <p class="pa-hint">Ser PLH é ter sido <strong>nomeado por um Cliente</strong>, em Administração → Clientes. Não se define aqui: o Cliente é a fonte única, e um mesmo Engenheiro pode responder por vários Clientes.</p>`)}

            ${block(f.hasAccess ? "6" : "5", "Assinatura", `
              <p class="pa-hint">A assinatura pertence ao Profissional e é cadastrada ao abrir a pessoa, depois de criada. Sem ela a pessoa existe normalmente, mas não pode aprovar nem assinar documentos.</p>`)}

            ${block(f.hasAccess ? "7" : "6", "Resumo", `
              <div class="pa-grid">
                <div class="pa-field"><span>Nome</span><strong>${this.esc(f.name) || "—"}</strong></div>
                <div class="pa-field"><span>Função</span><strong>${this.esc(f.functionTitle) || "—"}</strong></div>
                <div class="pa-field"><span>Profissão</span><strong>${this.esc(f.professionalType)}</strong></div>
                <div class="pa-field"><span>Acesso</span><strong>${f.hasAccess ? `Sim — ${this.esc(f.login) || "login não informado"}` : "Não"}</strong></div>
                <div class="pa-field"><span>Perfil</span><strong>${f.hasAccess ? this.esc(getRoleDisplayName(f.profileType)) : "—"}</strong></div>
                <div class="pa-field"><span>Situação</span><strong>${f.status === "ACTIVE" ? "Ativo" : "Inativo"}</strong></div>
              </div>`)}

            ${this.notice ? `<div class="pa-error">${this.esc(this.notice)}</div>` : ""}
            <div class="pa-actions">
              <button class="pa-btn" id="pa-cancel-new" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="pa-btn primary" id="pa-save-new" ${this.busy ? "disabled" : ""}>${this.busy ? "SALVANDO…" : "CADASTRAR PESSOA"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private renderNewAccessModal(): string {
    const row = this.rows.find(r => r.professionalId === this.showNewAccessFor);
    if (!row) return "";
    return `
      <div class="pa-modal-backdrop">
        <div class="pa-modal">
          <div class="pa-modal-head"><span>CRIAR ACESSO — ${this.esc(row.name)}</span><button class="pa-icon-btn" id="pa-close-access"><i data-lucide="x"></i></button></div>
          <div class="pa-modal-body">
            <p class="pa-hint">A conta será vinculada a esta pessoa. Nenhum cadastro novo de pessoa é criado.</p>
            <div class="pa-form">
              <label><span>Login *</span><input id="na-login" type="text"></label>
              <label><span>E-mail de acesso</span><input id="na-email" type="email" value="${this.esc(row.email)}"></label>
              <label><span>Perfil *</span><select id="na-role">${OFFICIAL_ROLES.map(r => `<option value="${r.code}">${r.label.toUpperCase()}</option>`).join("")}</select></label>
              <label><span>Senha inicial *</span><input id="na-password" type="password" autocomplete="new-password"></label>
            </div>
            ${this.notice ? `<div class="pa-error">${this.esc(this.notice)}</div>` : ""}
            <div class="pa-actions">
              <button class="pa-btn" id="pa-cancel-access" ${this.busy ? "disabled" : ""}>CANCELAR</button>
              <button class="pa-btn primary" id="pa-save-access" ${this.busy ? "disabled" : ""}>${this.busy ? "CRIANDO…" : "CRIAR ACESSO"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  // ------------------------------------------------------------- EVENTS

  private attachEvents() {
    this.querySelector("#pa-search")?.addEventListener("input", e => {
      this.search = (e.target as HTMLInputElement).value;
      const focus = () => { const el = this.querySelector("#pa-search") as HTMLInputElement | null; el?.focus(); el?.setSelectionRange(el.value.length, el.value.length); };
      this.render(); focus();
    });

    this.querySelectorAll("[data-filter]").forEach(btn => btn.addEventListener("click", e => {
      this.filter = (e.currentTarget as HTMLElement).getAttribute("data-filter") as PeopleFilter;
      this.render();
    }));

    this.querySelectorAll("[data-open]").forEach(btn => btn.addEventListener("click", e => {
      void this.openPerson((e.currentTarget as HTMLElement).getAttribute("data-open")!);
    }));

    this.querySelectorAll("[data-create-access]").forEach(btn => btn.addEventListener("click", e => {
      e.stopPropagation();
      this.showNewAccessFor = (e.currentTarget as HTMLElement).getAttribute("data-create-access");
      this.openPersonId = null;
      this.notice = "";
      this.render();
    }));

    this.querySelectorAll("[data-tab]").forEach(btn => btn.addEventListener("click", e => {
      this.detailTab = (e.currentTarget as HTMLElement).getAttribute("data-tab") as typeof this.detailTab;
      this.render();
    }));

    this.querySelector("#pa-close-detail")?.addEventListener("click", () => { this.openPersonId = null; this.detail = null; this.signatureError = ""; this.render(); });

    // ANEXAR ASSINATURA. A leitura vira data URL e vai inteira ao serviço,
    // que valida formato e tamanho — a tela filtra por conveniência (o
    // `accept` do input), nunca como controle: um arquivo arrastado ou
    // renomeado chegaria igual, e quem recusa é o serviço.
    this.querySelector("#pa-signature-file")?.addEventListener("change", async event => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = "";
      if (!file || !this.openPersonId) return;
      this.signatureError = "";
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result ?? ""));
          reader.onerror = () => reject(new Error("Não foi possível ler o arquivo selecionado."));
          reader.readAsDataURL(file);
        });
        await this.platform.adminService.saveProfessionalSignature(this.ctx(), this.openPersonId, dataUrl);
        // Recarrega o cadastro para a aba refletir o que ficou GRAVADO, e
        // não o que foi enviado: se o serviço normalizar algo, é o valor
        // dele que vale.
        await this.openPerson(this.openPersonId);
        this.detailTab = "ASSINATURA";
        this.notice = "Assinatura cadastrada.";
      } catch (e) {
        this.signatureError = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível cadastrar a assinatura.";
      }
      this.render();
    });

    this.querySelector("#pa-signature-remove")?.addEventListener("click", async () => {
      if (!this.openPersonId) return;
      // A remoção tira a pessoa do gate de PLH e de assinatura: confirma.
      if (!window.confirm("Remover a assinatura desta pessoa? Sem assinatura ela não poderá aprovar nem assinar documentos, nem ser PLH responsável de um Cliente.")) return;
      this.signatureError = "";
      try {
        await this.platform.adminService.removeProfessionalSignature(this.ctx(), this.openPersonId);
        await this.openPerson(this.openPersonId);
        this.detailTab = "ASSINATURA";
        this.notice = "Assinatura removida.";
      } catch (e) {
        this.signatureError = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível remover a assinatura.";
      }
      this.render();
    });
    this.querySelector("#btn-new-person")?.addEventListener("click", () => { this.showNewPerson = true; this.notice = ""; this.render(); });
    for (const id of ["#pa-close-new", "#pa-cancel-new"]) {
      this.querySelector(id)?.addEventListener("click", () => { this.showNewPerson = false; this.notice = ""; this.render(); });
    }
    for (const id of ["#pa-close-access", "#pa-cancel-access"]) {
      this.querySelector(id)?.addEventListener("click", () => { this.showNewAccessFor = null; this.notice = ""; this.render(); });
    }
    this.querySelectorAll("[data-resolve]").forEach(btn => btn.addEventListener("click", e => {
      const index = Number((e.currentTarget as HTMLElement).getAttribute("data-resolve"));
      void this.openPendencyResolution(index);
    }));
    for (const id of ["#pa-close-resolve", "#pa-cancel-resolve"]) {
      this.querySelector(id)?.addEventListener("click", () => {
        this.resolvingPendency = null; this.relink = null; this.relinkChoice = ""; this.notice = ""; this.render();
      });
    }
    this.querySelector("#pa-relink-choice")?.addEventListener("change", e => {
      this.relinkChoice = (e.target as HTMLSelectElement).value; this.render();
    });
    this.querySelector("#pa-confirm-resolve")?.addEventListener("click", () => void this.confirmRelink());

    // Atalho da listagem: abre a pessoa já com o cadastro em edição.
    this.querySelectorAll("[data-open-edit]").forEach(btn => btn.addEventListener("click", async e => {
      const id = (e.currentTarget as HTMLElement).getAttribute("data-open-edit")!;
      await this.openPerson(id);
      const p = this.detail?.professional;
      this.editingPerson = {
        name: String(p?.name ?? ""), email: String(p?.email ?? ""),
        functionTitle: String(p?.functionTitle ?? ""), professionalType: String(p?.professionalType ?? ProfessionalType.Inspector),
        crea: String(p?.crea ?? ""), cft: String(p?.cft ?? ""), snqc: String(p?.snqc ?? ""),
        status: String(p?.status ?? ProfessionalStatus.Active)
      };
      this.detailTab = "DADOS";
      this.render();
    }));

    // ABAS DADOS / ACESSO — editar o registro existente, sem recriar nada.
    this.querySelectorAll("[data-edit-person]").forEach(btn => btn.addEventListener("click", () => {
      const p = this.detail?.professional;
      this.editingPerson = {
        name: String(p?.name ?? ""), email: String(p?.email ?? ""),
        functionTitle: String(p?.functionTitle ?? ""), professionalType: String(p?.professionalType ?? ProfessionalType.Inspector),
        crea: String(p?.crea ?? ""), cft: String(p?.cft ?? ""), snqc: String(p?.snqc ?? ""),
        status: String(p?.status ?? ProfessionalStatus.Active)
      };
      this.notice = "";
      this.render();
    }));
    this.querySelectorAll("[data-cancel-edit-person]").forEach(btn => btn.addEventListener("click", () => {
      this.editingPerson = null; this.render();
    }));
    this.querySelectorAll("[data-person-field]").forEach(el => el.addEventListener("input", e => {
      const target = e.target as HTMLInputElement | HTMLSelectElement;
      const key = target.getAttribute("data-person-field") as keyof NonNullable<typeof this.editingPerson>;
      if (this.editingPerson && key) this.editingPerson[key] = target.value;
    }));
    this.querySelectorAll("[data-save-person]").forEach(btn => btn.addEventListener("click", e => {
      void this.savePerson((e.currentTarget as HTMLElement).getAttribute("data-save-person")!);
    }));

    this.querySelectorAll("[data-edit-access]").forEach(btn => btn.addEventListener("click", () => {
      const u = this.detail?.user;
      this.editingAccess = {
        login: String(u?.login ?? u?.username ?? ""), email: String(u?.email ?? ""),
        profileType: String(u?.profileType ?? RoleCode.Viewer),
        status: String(u?.status ?? "ACTIVE").toUpperCase() === "ACTIVE" ? "ACTIVE" : "INACTIVE"
      };
      this.notice = "";
      this.render();
    }));
    this.querySelectorAll("[data-cancel-edit-access]").forEach(btn => btn.addEventListener("click", () => {
      this.editingAccess = null; this.render();
    }));
    this.querySelectorAll("[data-access-field]").forEach(el => el.addEventListener("input", e => {
      const target = e.target as HTMLInputElement | HTMLSelectElement;
      const key = target.getAttribute("data-access-field") as keyof NonNullable<typeof this.editingAccess>;
      if (this.editingAccess && key) {
        this.editingAccess[key] = target.value;
        // Trocar o perfil revela o aviso do que muda — por isso re-renderiza.
        if (key === "profileType") this.render();
      }
    }));
    this.querySelectorAll("[data-save-access]").forEach(btn => btn.addEventListener("click", e => {
      void this.saveAccess((e.currentTarget as HTMLElement).getAttribute("data-save-access")!);
    }));
    this.querySelectorAll("[data-toggle-account]").forEach(btn => btn.addEventListener("click", e => {
      void this.toggleAccount((e.currentTarget as HTMLElement).getAttribute("data-toggle-account")!);
    }));

    // ABA PERMISSÕES — busca, filtro por domínio, salvar e restaurar.
    this.querySelector("#perm-search")?.addEventListener("input", e => {
      this.permissionQuery = (e.target as HTMLInputElement).value;
      this.render();
      // A busca re-renderiza a aba inteira; devolver o cursor evita que o
      // usuário tenha de clicar de novo a cada letra digitada.
      const input = this.querySelector("#perm-search") as HTMLInputElement | null;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    });
    this.querySelectorAll("[data-perm-group]").forEach(btn => btn.addEventListener("click", e => {
      this.permissionGroup = (e.currentTarget as HTMLElement).getAttribute("data-perm-group") as PermissionGroupKey | "ALL";
      this.render();
    }));
    this.querySelectorAll("[data-perm-save]").forEach(btn => btn.addEventListener("click", e => {
      void this.savePermissions((e.currentTarget as HTMLElement).getAttribute("data-perm-save")!);
    }));
    this.querySelectorAll("[data-perm-restore]").forEach(btn => btn.addEventListener("click", e => {
      void this.restorePermissions((e.currentTarget as HTMLElement).getAttribute("data-perm-restore")!);
    }));
    this.querySelectorAll("[data-reset-password]").forEach(btn => btn.addEventListener("click", e => {
      const userId = (e.currentTarget as HTMLElement).getAttribute("data-reset-password")!;
      const row = this.rows.find(r => r.userId === userId);
      this.resetPasswordFor = { userId, personName: row?.name || "esta pessoa", login: row?.username || "" };
      this.notice = "";
      this.render();
    }));
    for (const id of ["#pa-close-reset", "#pa-cancel-reset"]) {
      this.querySelector(id)?.addEventListener("click", () => { this.resetPasswordFor = null; this.notice = ""; this.render(); });
    }
    this.querySelector("#pa-confirm-reset")?.addEventListener("click", () => void this.confirmResetPassword());
    this.querySelector("#pa-legacy-apply")?.addEventListener("click", () => void this.applyLegacyPlhCleanup());
    this.querySelector("#np-has-access")?.addEventListener("change", e => {
      this.syncNewPersonForm();
      this.newPerson.hasAccess = (e.target as HTMLInputElement).checked;
      this.render();
    });
    this.querySelector("#pa-save-new")?.addEventListener("click", () => void this.createPerson());
    this.querySelector("#pa-save-access")?.addEventListener("click", () => void this.createAccess());
  }

  private value(selector: string): string {
    return ((this.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null)?.value || "").trim();
  }

  private async openPendencyResolution(index: number) {
    const pendency = this.pendencies[index];
    if (!pendency?.userId) return;
    this.resolvingPendency = pendency;
    this.relink = null;
    this.relinkChoice = "";
    this.notice = "";
    this.render();
    try {
      const resolution = await this.platform.adminService.resolveUserProfessionalLink(this.ctx(), pendency.userId);
      this.relink = resolution;
      // Candidato único e inequívoco vem pré-selecionado; havendo dúvida,
      // nada é escolhido por conta própria.
      this.relinkChoice = resolution.automatic ? resolution.automatic.professionalId : "";
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível analisar a pendência.";
    }
    this.render();
  }

  /**
   * Grava as permissões INDIVIDUAIS marcadas na aba.
   *
   * Só chegam aqui as caixas habilitadas — as herdadas do perfil estão
   * `disabled`, porque desmarcá-las não teria efeito
   * (`resolveEffectivePermissions` soma perfil + individuais, sem negação).
   * O servidor revalida: `updateUser` exige a permissão de administração, e
   * esconder o botão nunca foi o controle.
   */
  /** Grava o Professional EXISTENTE. `professionalId` nunca muda. */
  private async savePerson(professionalId: string) {
    if (this.busy || !this.editingPerson) return;
    const f = this.editingPerson;
    if (!f.name.trim()) { this.notice = "Informe o nome da pessoa."; this.render(); return; }

    this.busy = true; this.notice = ""; this.render();
    try {
      const current = this.detail?.professional;
      if (!current) throw new AppError("VALIDATION_ERROR" as never, "sem professional", { safeMessage: "Cadastro não carregado." });
      // Espalha o registro atual e sobrescreve só o que o formulário edita:
      // qualquer campo que esta tela não conhece é preservado como está.
      await this.platform.adminService.updateProfessional(this.ctx(), {
        ...current,
        name: f.name.trim(), email: f.email.trim(), functionTitle: f.functionTitle.trim(),
        professionalType: f.professionalType, crea: f.crea.trim(), cft: f.cft.trim(), snqc: f.snqc.trim(),
        status: f.status
      });
      this.editingPerson = null;
      this.notice = "Cadastro atualizado. O identificador do Profissional e todos os vínculos foram preservados.";
      await this.load();
      await this.openPerson(professionalId);
      this.detailTab = "DADOS";
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível salvar o cadastro.";
    }
    this.busy = false; this.render();
  }

  /** Grava a conta EXISTENTE. `userId` e o vínculo com o Professional não mudam. */
  private async saveAccess(userId: string) {
    if (this.busy || !this.editingAccess) return;
    const f = this.editingAccess;
    if (!f.login.trim()) { this.notice = "Informe o login."; this.render(); return; }

    this.busy = true; this.notice = ""; this.render();
    try {
      const current = this.detail?.user;
      if (!current) throw new AppError("VALIDATION_ERROR" as never, "sem user", { safeMessage: "Conta não carregada." });
      await this.platform.adminService.updateUser(this.ctx(), {
        ...current,
        login: f.login.trim(), username: f.login.trim(), email: f.email.trim(),
        profileType: f.profileType, status: f.status
      });
      const changedRole = f.profileType !== String(current.profileType);
      this.editingAccess = null;
      this.notice = changedRole
        ? `Acesso atualizado. Perfil alterado para ${f.profileType} — as permissões herdadas são recalculadas no próximo carregamento da sessão da pessoa, e as individuais foram preservadas.`
        : "Acesso atualizado. O identificador do usuário e o vínculo com o Profissional foram preservados.";
      await this.load();
      await this.openPerson(this.openPersonId || "");
      this.detailTab = "ACESSO";
    } catch (e) {
      // Login/e-mail duplicado chega aqui com a mensagem do serviço.
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível salvar o acesso.";
    }
    this.busy = false; this.render();
  }

  /**
   * INATIVAR / REATIVAR a conta — sem apagar nada.
   *
   * Inativar bloqueia o acesso e preserva Professional, documentos,
   * assinaturas e auditoria. Reativar devolve o acesso com os mesmos
   * identificadores. Excluir para "tirar o acesso" levaria junto o histórico.
   */
  private async toggleAccount(userId: string) {
    if (this.busy) return;
    const current = this.detail?.user;
    if (!current) return;
    const active = String(current.status ?? "ACTIVE").toUpperCase() === "ACTIVE";
    const verb = active ? "inativar" : "reativar";
    if (!window.confirm(`Confirma ${verb} a conta de acesso desta pessoa? O Profissional, os documentos e o histórico são preservados em qualquer caso.`)) return;

    this.busy = true; this.notice = ""; this.render();
    try {
      await this.platform.adminService.updateUser(this.ctx(), { ...current, status: active ? "INACTIVE" : "ACTIVE" });
      this.notice = active
        ? "Conta inativada. A pessoa não entra mais no sistema; o Profissional e todo o histórico continuam intactos."
        : "Conta reativada, com os mesmos identificadores.";
      await this.load();
      await this.openPerson(this.openPersonId || "");
      this.detailTab = "ACESSO";
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : `Não foi possível ${verb} a conta.`;
    }
    this.busy = false; this.render();
  }

  private async savePermissions(userId: string) {
    if (this.busy) return;
    const checked = [...this.querySelectorAll('[data-perm-code]')]
      .filter(el => (el as HTMLInputElement).checked && !(el as HTMLInputElement).disabled)
      .map(el => (el as HTMLElement).getAttribute("data-perm-code")!);

    this.busy = true;
    this.notice = "";
    this.render();
    try {
      const user = this.detail?.user;
      if (!user) throw new AppError("VALIDATION_ERROR" as never, "sem usuário", { safeMessage: "Conta de acesso não carregada." });
      await this.platform.adminService.updateUser(this.ctx(), { ...user, customPermissions: checked });
      this.notice = `Permissões individuais salvas: ${checked.length}. A pessoa verá a mudança no próximo carregamento da sessão dela.`;
      await this.openPerson(this.openPersonId || String(userId));
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível salvar as permissões.";
    }
    this.busy = false;
    this.render();
  }

  /** Remove TODAS as permissões individuais — a pessoa fica só com o perfil. */
  private async restorePermissions(userId: string) {
    if (this.busy) return;
    const user = this.detail?.user;
    const count = (user?.customPermissions ?? []).length;
    if (count === 0) { this.notice = "Esta pessoa já usa apenas as permissões do perfil."; this.render(); return; }
    if (!window.confirm(`Remover as ${count} permissão(ões) individual(is) desta pessoa? Ela passará a ter exatamente o que o perfil ${user.profileType} concede.`)) return;

    this.busy = true;
    this.notice = "";
    this.render();
    try {
      await this.platform.adminService.updateUser(this.ctx(), { ...user, customPermissions: [] });
      this.notice = "Permissões individuais removidas. A pessoa passou a ter apenas as do perfil.";
      await this.openPerson(this.openPersonId || String(userId));
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível restaurar as permissões do perfil.";
    }
    this.busy = false;
    this.render();
  }

  private async confirmRelink() {
    const pendency = this.resolvingPendency;
    if (!pendency?.userId || !this.relinkChoice || this.busy) return;
    this.busy = true; this.notice = ""; this.render();
    try {
      await this.platform.adminService.relinkUserToProfessional(this.ctx(), pendency.userId, this.relinkChoice);
      this.resolvingPendency = null; this.relink = null; this.relinkChoice = "";
      this.busy = false;
      await this.load();
      this.notice = `Vínculo corrigido: a conta de ${pendency.name} passou a apontar para o cadastro correto.`;
      this.render();
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível corrigir o vínculo.";
      this.busy = false;
      this.render();
    }
  }

  /** Lê o formulário para o estado, para que nada se perca no re-render. */
  private syncNewPersonForm() {
    const f = this.newPerson;
    const read = (sel: string, current: string) => {
      const el = this.querySelector(sel) as HTMLInputElement | HTMLSelectElement | null;
      return el ? el.value : current;
    };
    f.name = read("#np-name", f.name);
    f.email = read("#np-email", f.email);
    f.status = read("#np-status", f.status);
    f.functionTitle = read("#np-function", f.functionTitle);
    f.professionalType = read("#np-type", f.professionalType);
    f.crea = read("#np-crea", f.crea);
    f.cft = read("#np-cft", f.cft);
    f.snqc = read("#np-snqc", f.snqc);
    if (f.hasAccess) {
      f.login = read("#np-login", f.login);
      f.accessEmail = read("#np-access-email", f.accessEmail);
      f.password = read("#np-password", f.password);
      f.profileType = read("#np-role", f.profileType);
    }
  }

  /**
   * Cadastra a pessoa e, quando pedido, a conta — numa passagem só.
   *
   * A conta é criada DEPOIS do Profissional e amarrada a ele por
   * `createAccessForProfessional`, que é o caminho que não duplica pessoa.
   * Se a criação da conta falhar, o Profissional já existe e é dito com
   * todas as letras: nada é desfeito pelas costas, e a conta pode ser criada
   * de novo pelo botão da linha.
   */
  private async createPerson() {
    if (this.busy) return;
    this.syncNewPersonForm();
    const f = this.newPerson;
    if (!f.name.trim() || !f.functionTitle.trim()) {
      this.notice = "Informe pelo menos o nome e a função."; this.render(); return;
    }
    if (f.hasAccess && (!f.login.trim() || !f.password)) {
      this.notice = "Para criar o acesso, informe o login e a senha inicial."; this.render(); return;
    }

    this.busy = true; this.notice = ""; this.render();
    let created: { id: string } | null = null;
    try {
      created = await this.platform.adminService.createProfessional(this.ctx(), {
        name: f.name.trim(), email: f.email.trim(), functionTitle: f.functionTitle.trim(),
        professionalType: f.professionalType,
        crea: f.crea.trim() || null,
        cft: f.cft.trim() || null,
        snqc: f.snqc.trim() || null
      });
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível cadastrar a pessoa.";
      this.busy = false; this.render(); return;
    }

    let accessNotice = "";
    if (f.hasAccess && created) {
      try {
        await this.platform.adminService.createAccessForProfessional(this.ctx(), String(created.id), {
          login: f.login.trim(), email: (f.accessEmail || f.email).trim(),
          password: f.password, profileType: f.profileType
        });
        accessNotice = ` Conta '${f.login.trim()}' criada e vinculada.`;
      } catch (e) {
        const reason = e instanceof AppError ? ((e as any).safeMessage || e.message) : "erro desconhecido";
        accessNotice = ` A pessoa foi cadastrada, mas a CONTA não: ${reason} Use "Criar acesso" na linha dela.`;
      }
    }

    this.showNewPerson = false;
    this.newPerson = {
      name: "", email: "", status: "ACTIVE",
      functionTitle: "", professionalType: String(ProfessionalType.Inspector), crea: "", cft: "", snqc: "",
      hasAccess: false, login: "", accessEmail: "", password: "", profileType: String(RoleCode.Viewer)
    };
    this.busy = false;
    await this.load();
    this.notice = `${f.name.trim()} foi cadastrada.${accessNotice}`;
    this.render();
  }

  private async createAccess() {
    if (this.busy || !this.showNewAccessFor) return;
    const login = this.value("#na-login");
    const password = this.value("#na-password");
    if (!login || !password) { this.notice = "Informe o login e a senha inicial."; this.render(); return; }

    this.busy = true; this.notice = ""; this.render();
    const professionalId = this.showNewAccessFor;
    try {
      await this.platform.adminService.createAccessForProfessional(this.ctx(), professionalId, {
        login, email: this.value("#na-email"), password, profileType: this.value("#na-role")
      });
      this.showNewAccessFor = null;
      this.busy = false;
      await this.load();
      this.notice = `Acesso criado e vinculado a ${this.rows.find(r => r.professionalId === professionalId)?.name ?? "a pessoa"}.`;
      this.render();
    } catch (e) {
      this.notice = e instanceof AppError ? ((e as any).safeMessage || e.message) : "Não foi possível criar o acesso.";
      this.busy = false;
      this.render();
    }
  }

  private styles(): string {
    return `
      app-people-access-page { display:block; padding:24px 28px; color: var(--ia-fg, #E6E9EF); }
      .pa-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:18px; }
      .pa-head h1 { font-size:1.35rem; margin:0 0 4px; }
      .pa-head p { margin:0; font-size:0.82rem; color: var(--ia-fg-soft, #A7B0C0); max-width:70ch; }
      .pa-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border: 1px solid var(--ia-line, #2B3340); background: var(--ia-bg-panel, #1B212C); color: var(--ia-fg, #E6E9EF); font-size:0.8rem; font-weight:600; cursor:pointer; }
      .pa-btn.primary { background:#7c3aed; border-color:#7c3aed; color:#fff; }
      .pa-btn.ghost { background:transparent; }
      .pa-btn.sm { padding:5px 10px; font-size:0.72rem; }
      .pa-btn[disabled] { opacity:0.5; cursor:not-allowed; }
      .pa-icon-btn { background:none; border:none; color: var(--ia-fg-muted, #8A94A6); cursor:pointer; }
      .pa-toolbar { display:flex; flex-wrap:wrap; gap:12px; align-items:center; margin-bottom:14px; }
      .pa-search { display:flex; align-items:center; gap:8px; background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:6px 10px; min-width:320px; }
      .pa-search input { background:transparent; border:none; color: var(--ia-fg, #E6E9EF); outline:none; width:100%; font-size:0.82rem; }
      .pa-filters { display:flex; flex-wrap:wrap; gap:6px; }
      .pa-chip { padding:5px 12px; border-radius:999px; border: 1px solid var(--ia-line, #2B3340); background:transparent; color: var(--ia-fg-soft, #A7B0C0); font-size:0.72rem; cursor:pointer; }
      .pa-chip.active { background:#7c3aed; border-color:#7c3aed; color:#fff; font-weight:700; }
      .pa-table { width:100%; border-collapse:collapse; font-size:0.8rem; }
      .pa-table th { text-align:left; padding:8px 10px; border-bottom: 1px solid var(--ia-line, #2B3340); color: var(--ia-fg-muted, #8A94A6); font-size:0.68rem; text-transform:uppercase; letter-spacing:0.05em; }
      .pa-table td { padding:9px 10px; border-bottom:1px solid #1C2230; vertical-align:middle; }
      .pa-table tbody tr:hover { background: var(--ia-bg-panel, #141924); }
      .pa-name { font-weight:600; }
      .pa-row-actions { display:flex; gap:6px; justify-content:flex-end; }
      .pa-tag { display:inline-block; padding:2px 9px; border-radius:999px; font-size:0.68rem; font-weight:700; }
      .pa-tag.ok { background:rgba(16,185,129,0.16); color: var(--ia-success-fg, #a7f3d0); }
      .pa-tag.warn { background:rgba(245,158,11,0.16); color: var(--ia-warning-fg, #fde68a); }
      .pa-tag.muted { background:rgba(148,163,184,0.14); color: var(--ia-fg, #cbd5e1); }
      .pa-count { margin-top:10px; font-size:0.74rem; color: var(--ia-fg-muted, #8A94A6); }
      .pa-empty { padding:40px; text-align:center; color: var(--ia-fg-muted, #8A94A6); font-size:0.85rem; }
      .pa-empty-inline { padding:18px; background: var(--ia-bg-panel, #131822); border: 1px solid var(--ia-line, #232A38); border-radius:8px; display:flex; flex-direction:column; gap:10px; align-items:flex-start; }
      .pa-notice { padding:10px 14px; border-radius:6px; background:rgba(96,165,250,0.12); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); margin-bottom:12px; font-size:0.8rem; }
      .pa-error { padding:10px 14px; border-radius:6px; background:rgba(239,68,68,0.12); border:1px solid rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); margin-bottom:12px; font-size:0.8rem; }
      .pa-warn { margin-top:12px; padding:10px 14px; border-radius:6px; background:rgba(245,158,11,0.12); border:1px solid rgba(245,158,11,0.35); color: var(--ia-warning-fg, #fde68a); font-size:0.8rem; }
      .pa-warn p { margin:4px 0 0; }
      .pa-pendency { display:flex; gap:12px; padding:12px 16px; border-radius:8px; background:rgba(245,158,11,0.1); border:1px solid rgba(245,158,11,0.32); color: var(--ia-warning-fg, #fde68a); margin-bottom:16px; font-size:0.8rem; }
      .pa-pendency p { margin:4px 0; opacity:0.9; }
      .pa-pendency ul { margin:6px 0 0; padding-left:18px; }
      .pa-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10002; padding:20px; }
      .pa-modal { background: var(--ia-bg-panel, #161A22); border: 1px solid var(--ia-line, #262C38); border-radius:12px; width:100%; max-width:560px; max-height:92vh; display:flex; flex-direction:column; overflow:hidden; }
      .pa-modal.wide { max-width:860px; }
      .pa-modal-head { padding:12px 18px; border-bottom:1px solid #262C38; display:flex; justify-content:space-between; align-items:center; font-weight:700; font-size:0.9rem; }
      .pa-modal-body { padding:16px 18px; overflow:auto; display:flex; flex-direction:column; gap:12px; }
      .pa-tabs { display:flex; gap:4px; padding:0 14px; border-bottom:1px solid #262C38; flex-wrap:wrap; }
      .pa-tab { padding:9px 12px; background:none; border:none; border-bottom:2px solid transparent; color: var(--ia-fg-muted, #8A94A6); font-size:0.78rem; cursor:pointer; }
      .pa-tab.active { color: var(--ia-fg, #E6E9EF); border-bottom-color:#7c3aed; font-weight:600; }
      .pa-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(200px,1fr)); gap:12px; }
      .pa-field { display:flex; flex-direction:column; gap:3px; font-size:0.7rem; color: var(--ia-fg-muted, #8A94A6); }
      .pa-field strong { font-size:0.85rem; color: var(--ia-fg, #E6E9EF); font-weight:600; }
      .pa-form { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px,1fr)); gap:12px; }
      .pa-form label { display:flex; flex-direction:column; gap:4px; font-size:0.7rem; color: var(--ia-fg-soft, #A7B0C0); }
      .pa-form input, .pa-form select { background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:8px 10px; color: var(--ia-fg, #E6E9EF); font-size:0.82rem; }
      .pa-actions { display:flex; justify-content:flex-end; gap:8px; }
      .pa-hint { font-size:0.74rem; color: var(--ia-fg-muted, #8A94A6); margin:0; line-height:1.5; }
      .pa-block { border:1px solid #232A38; border-radius:8px; padding:12px 14px; display:flex; flex-direction:column; gap:10px; }
      .pa-block-head { display:flex; align-items:center; gap:8px; font-size:0.78rem; font-weight:700; letter-spacing:0.03em; text-transform:uppercase; color: var(--ia-fg, #C7CEDA); }
      .pa-block-num { display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:50%; background:#7c3aed; color:#fff; font-size:0.68rem; }
      .pa-toggle { display:flex; align-items:center; gap:8px; font-size:0.8rem; font-weight:600; cursor:pointer; }
      .pa-legacy { border:1px solid rgba(245,158,11,0.32); background:rgba(245,158,11,0.08); border-radius:8px; padding:12px 16px; margin-bottom:16px; display:flex; flex-direction:column; gap:10px; }
      .pa-legacy-head { font-size:0.75rem; font-weight:700; letter-spacing:0.05em; color: var(--ia-warning-fg, #fde68a); }
      .pa-legacy-group { border-top:1px solid rgba(245,158,11,0.2); padding-top:10px; display:flex; flex-direction:column; gap:6px; align-items:flex-start; }
      .pa-legacy-group ul { margin:0; padding-left:18px; font-size:0.78rem; color: var(--ia-fg, #C7CEDA); line-height:1.6; }
      .pa-perm-box { background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #232A38); border-radius:6px; padding:10px 12px; font-family:var(--font-mono, monospace); font-size:0.7rem; color: var(--ia-fg-soft, #A7B0C0); line-height:1.7; }
      /* Aba PERMISSÕES: busca, filtros por domínio e a lista agrupada. */
      .pa-perm-toolbar { display:flex; flex-wrap:wrap; gap:10px; align-items:center; margin:12px 0; }
      .pa-perm-toolbar .pa-input { flex:1 1 240px; }
      .pa-chips { display:flex; flex-wrap:wrap; gap:6px; }
      .pa-perm-group { margin-bottom:14px; }
      .pa-perm-group-head { font-size:0.7rem; font-weight:700; letter-spacing:0.05em; text-transform:uppercase; color: var(--ia-fg-soft, #A7B0C0); padding:6px 0; border-bottom: 1px solid var(--ia-line, #232A38); margin-bottom:4px; }
      .pa-perm-group-head small { color: var(--ia-fg-muted, #6B7480); font-weight:400; margin-left:6px; }
      .pa-perm-line { display:grid; grid-template-columns:18px 14px 1fr auto; align-items:center; gap:8px; padding:5px 6px; border-radius:4px; font-size:0.78rem; color: var(--ia-fg, #C9D1DB); }
      .pa-perm-line:hover { background: var(--ia-bg-panel, #141A22); }
      .pa-perm-line.on { color: var(--ia-fg, #EDEDED); }
      .pa-perm-line input[disabled] { cursor:not-allowed; }
      .pa-perm-mark { color:#7c3aed; font-weight:700; text-align:center; }
      .pa-perm-line code { font-family:var(--font-mono, monospace); font-size:0.66rem; color: var(--ia-fg-muted, #6B7480); }
      /* Aviso de troca de perfil: diz o que muda ANTES de salvar. */
      .pa-warn-box { border:1px solid rgba(245,158,11,0.45); background:rgba(245,158,11,0.10); color: var(--ia-warning-fg, #fde68a); border-radius:6px; padding:10px 12px; margin:12px 0; font-size:0.78rem; }
      .pa-warn-box strong { display:block; margin-bottom:4px; font-size:0.74rem; letter-spacing:0.03em; }
      .pa-warn-box p { margin:0; line-height:1.5; }
      .pa-signature { display:flex; justify-content:center; padding:14px; background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #232A38); border-radius:8px; }
      .pa-signature img { max-height:140px; max-width:100%; background:#fff; border-radius:4px; padding:6px; }
      .pa-signature-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:12px; }
      /* O rótulo aciona o input escondido: mesma aparência dos demais botões. */
      .pa-signature-actions label.pa-btn { cursor:pointer; }
      .pa-btn-ghost { background:transparent; }
      .pa-clients ul { list-style:none; margin:8px 0 0; padding:0; display:flex; flex-direction:column; gap:6px; }
      .pa-clients li { display:flex; align-items:center; gap:8px; font-size:0.82rem; }
      @media (max-width: 900px) { .pa-table { display:block; overflow-x:auto; } }
    `;
  }
}

if (!customElements.get("app-people-access-page")) {
  customElements.define("app-people-access-page", PeopleAccessPage);
}
